import * as React from 'react'
import { useMemo, useState } from 'react'

import { ENTITY_TYPES, getFormattedAddress } from '@sales-center/common'
import { IOpportunity } from '@user/store/opportunity/typings'
import { OPPORTUNITY_STATUSES, OppStatus } from './typings'
import { ParentHeader } from './ParentHeader'
import { RequirementsToClose } from '@user/components/RequirementsToClose'
import { useSelectFunnelStagesByType } from '@user/store/salesFunnel/hooks'

import { ContentRight } from '@user/components/EntityCards/atoms/ContentRight'
import { EntityCardBody } from '@user/components/EntityCards/Body'
import { EntityCardDrawer } from '@user/components/EntityCards/Drawer'
import { EntityCardFooter } from '@user/components/EntityCards/Footer'
import { CancelServicesButton } from '@user/components/Opportunities/OpportunityActions/CancelServicesButton'
import { CancelServicesDialog } from '@user/components/SoldServices/CancelServices'
import { EntityCardHeader } from '@user/components/EntityCards/Header'
import { EntityCardWrapper } from '@user/components/EntityCards/Wrapper'
import { EntityName } from '@user/components/EntityCards/atoms/EntityName'

import { EntityPrimaryContactDrawer } from '@user/components/EntityCards/atoms/EntityPrimaryContactDrawer'
import { SoldServicesCardFooter } from '@user/components/SoldServices/SoldServicesCardFooter/SoldServicesCardFooter'
import { useToggleBoolean } from '@user/utils/useToggleBoolean'

import Box from '@mui/material/Box'
import {
  ReOpenPopupVariant,
  soldServiceStatusToReOpenPopupVariant,
} from '../ReOpenOppPopup/typings'
import { Appointments } from '@user/components/Scheduling/Estimates'
import { LocationsPopoverButton } from '@user/components/EntityCards/Locations'
import { SchedulingWizardContextProvider } from '@user/components/Scheduling'
import Stack from '@mui/material/Stack'
import { useSchedulingVisibility } from '@user/store/closingRequirements'
import { CWQDialogContextProvider } from '@user/components/SoldServices'
import { ReOpenOppPopupNew } from '@user/components/Opportunities/ReOpenOppPopup/ReOpenOppPopupNew'
import LocationContextProvider from '@user/components/EntityCards/Locations/LocationsPopoverButton/context/LocationContextProvider'
import {
  MostRecentKnockStatus,
  getKnockStatusFromCard,
} from '@user/features/MapView/MostRecentKnockStatus'

interface Props {
  opportunity: IOpportunity
  canUpdateOpps: boolean
  canDeleteOpps: boolean
  hasScheduledAppointment: boolean
}

export function OpportunityCard(props: Props) {
  const { opportunity, canDeleteOpps, canUpdateOpps, hasScheduledAppointment } =
    props

  const {
    address,
    address2,
    branch,
    branchId,
    city,
    closed,
    closedDate,
    closedLostReason,
    externalLocationCode,
    externalLocationId,
    id,
    opportunityName,
    postalCode,
    salesFunnelId,
    salesFunnelStageId,
    salesTeamId,
    servicesCount,
    state,
    closingRequirements,
    primaryContact,
    referralDiscountId,
    soldServiceStatus,
    useSystemName,
    hasValidLocationAddress,
    scheduleSettings,
    services,
    hasValidPropertyAddress,
  } = opportunity

  const [showReOpenPopup, setShowReOpenPopup] = useState<boolean>(false)
  const stages = useSelectFunnelStagesByType(salesFunnelId, 'Opportunity')

  const currentStage = useMemo(
    () => stages.filter((s) => s.id == salesFunnelStageId).shift(),
    [salesFunnelStageId, stages],
  )

  const fullAddress = useMemo(() => {
    return getFormattedAddress(address, address2, city, state, postalCode)
  }, [address, address2, city, state, postalCode])

  const oppStatus = React.useMemo((): OppStatus => {
    if (closedDate == null || !currentStage) return OPPORTUNITY_STATUSES.Open

    const { name } = currentStage

    switch (name) {
      case 'Closed/Won':
        return OPPORTUNITY_STATUSES.Won
      case 'Closed/Lost':
        return OPPORTUNITY_STATUSES.Lost
      default:
        return OPPORTUNITY_STATUSES.Open
    }
  }, [currentStage, closedDate])

  const schedulingVisible = useSchedulingVisibility(scheduleSettings)

  const showApptChip =
    oppStatus === OPPORTUNITY_STATUSES.Open &&
    hasScheduledAppointment &&
    schedulingVisible

  const reOpenPopupVariant: ReOpenPopupVariant = useMemo(
    () => soldServiceStatusToReOpenPopupVariant(soldServiceStatus),
    [soldServiceStatus],
  )

  const [open, toggleOpen] = useToggleBoolean(false)

  return (
    <CWQDialogContextProvider>
      <SchedulingWizardContextProvider
        entityId={id}
        entityType={ENTITY_TYPES.Opportunity}
        salesFunnelId={salesFunnelId}
      >
        <LocationContextProvider
          entityId={id}
          entityType={ENTITY_TYPES.Opportunity}
          locations={[]}
        >
          <MostRecentKnockStatus
            variant="card"
            knockStatus={getKnockStatusFromCard(
              opportunity.knockStatus,
              opportunity.knockStatusReason,
            )}
          />
          <EntityCardWrapper>
            <EntityCardHeader>
              <ParentHeader
                {...opportunity}
                closeWinDisabled={!opportunity.isCloseable}
                locationId={opportunity.externalLocationId}
                oppStatus={oppStatus}
                opportunityId={id}
                closeWinErrors={[]}
                branch={branch}
                salesTeamId={salesTeamId}
                canUpdate={canUpdateOpps}
                canDelete={canDeleteOpps}
                opportunity={opportunity}
              />
            </EntityCardHeader>

            <EntityCardBody>
              <Box display="flex" justifyContent="space-between">
                <Box>
                  <EntityName
                    label="Opportunity"
                    labelPrefix="Contact"
                    name={primaryContact.contactName}
                    subName={opportunityName}
                    entityId={id}
                    detailUrl={`/opportunities/${id}`}
                  />

                  <Stack direction="row" alignItems="flex-start">
                    <LocationsPopoverButton
                      buttonStyle="chip"
                      entityId={id}
                      entityType={ENTITY_TYPES.Opportunity}
                      entityBranchId={branchId}
                      canUpdateEntity={canUpdateOpps}
                      matched={externalLocationCode !== null}
                      variant="entity"
                    />
                    {showApptChip && (
                      <Box ml={1} mt={1.4}>
                        <Appointments
                          entityId={id}
                          entityType={ENTITY_TYPES.Opportunity}
                        />
                      </Box>
                    )}
                  </Stack>
                </Box>

                <ContentRight
                  detail={opportunity}
                  entityId={id}
                  entityStatus={oppStatus}
                  entityType={ENTITY_TYPES.Opportunity}
                />
              </Box>

              <EntityPrimaryContactDrawer
                contact={primaryContact}
                entityId={id}
                entityType={ENTITY_TYPES.Opportunity}
                canUpdate={canUpdateOpps}
                hasValidPropertyAddress={hasValidPropertyAddress}
              />
            </EntityCardBody>

            <EntityCardFooter
              entityId={id}
              entityType={ENTITY_TYPES.Opportunity}
              schedulingVisible={schedulingVisible}
              handleOpenDrawer={toggleOpen}
              isOpen={open}
              entityDetails={{
                services,
                closedDate,
                reason: closedLostReason,
                servicesCount,
                status: oppStatus,
                closingRequirements,
              }}
              closedAction={
                oppStatus === OPPORTUNITY_STATUSES.Lost ? (
                  <CancelServicesButton
                    opportunityId={id}
                    soldServiceStatus={soldServiceStatus}
                  />
                ) : undefined
              }
            />

            <EntityCardDrawer isOpen={open}>
              <RequirementsToClose
                address={fullAddress}
                branchId={branchId}
                entityId={id}
                entityType={ENTITY_TYPES.Opportunity}
                externalLocationId={externalLocationId}
                externalBillToId={opportunity.billToContact.externalLinkId}
                oppStatus={oppStatus}
                services={services}
                servicesCount={servicesCount}
                salesFunnelId={salesFunnelId}
                canUpdate={canUpdateOpps}
                referralDiscountId={referralDiscountId}
                requirementsAndCounts={closingRequirements}
                scheduleSettings={scheduleSettings}
                useSystemName={useSystemName}
                hasValidLocationAddress={hasValidLocationAddress}
              />
            </EntityCardDrawer>

            {closed && soldServiceStatus !== 'NotSent' && (
              <SoldServicesCardFooter
                onReOpenClick={() => setShowReOpenPopup(true)}
                opportunityId={id}
                soldServiceStatus={soldServiceStatus}
              />
            )}

            {oppStatus === OPPORTUNITY_STATUSES.Lost && (
              <CancelServicesDialog opportunityId={id} />
            )}
          </EntityCardWrapper>

          {showReOpenPopup && (
            <ReOpenOppPopupNew
              onClose={() => setShowReOpenPopup(false)}
              open={showReOpenPopup}
              opportunityId={opportunity.id}
              variant={reOpenPopupVariant}
              salesFunnelId={salesFunnelId}
              salesTeamId={salesTeamId.toString()}
              assigneeId={
                opportunity.assignee?.id ===
                '00000000-0000-0000-0000-000000000000'
                  ? null
                  : opportunity.assignee?.id || null
              }
            />
          )}
        </LocationContextProvider>
      </SchedulingWizardContextProvider>
    </CWQDialogContextProvider>
  )
}
